import React from 'react';
import ResponsiveSelect from '../../../../components/ResponsiveSelect';
import {
  TextField,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Typography,
  RadioGroup,
  FormControl,
  FormLabel,
  FormGroup
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

interface BankFieldPreviewProps {
  label: string;
  type: string;
  required: boolean;
  options: string[];
}

const BankFieldPreview: React.FC<BankFieldPreviewProps> = ({
  label,
  type,
  required,
  options
}) => {
  const displayLabel = label || 'Field Preview';

  const renderField = () => {
    switch (type) {
      case 'text':
        return (
          <TextField
            fullWidth
            label={displayLabel}
            required={required}
            variant="outlined"
            placeholder="Text input preview"
            size="small"
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            label={displayLabel}
            required={required}
            type="number"
            variant="outlined"
            placeholder="Number input preview"
            size="small"
          />
        );
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker format="DD/MM/YYYY"
              label={displayLabel}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: required,
                  size: 'small'
                }
              }}
            />
          </LocalizationProvider>
        );
      case 'dropdown':
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [selectedValue, setSelectedValue] = React.useState<string>('');

        return (
          <ResponsiveSelect
            label={displayLabel}
            value={selectedValue}
            onChange={(val) => setSelectedValue(val)}
            options={options}
            required={required}
          />
        );
      case 'radio':
        if (options && options.length > 0) {
          return (
            <FormControl component="fieldset">
              <FormLabel component="legend">{displayLabel}{required ? ' *' : ''}</FormLabel>
              <RadioGroup>
                {options.map((opt, idx) => (
                  <FormControlLabel
                    key={idx}
                    value={opt}
                    control={<Radio size="small" />}
                    label={opt}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )
        }
        return (
          <FormControlLabel
            control={<Radio size="small" />}
            label={<Typography variant="body2">{displayLabel}{required ? ' *' : ''}</Typography>}
          />
        );
      case 'checkbox':
        if (options && options.length > 0) {
          return (
            <FormControl component="fieldset">
              <FormLabel component="legend">{displayLabel}{required ? ' *' : ''}</FormLabel>
              <FormGroup>
                {options.map((opt, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={<Checkbox size="small" />}
                    label={opt}
                  />
                ))}
              </FormGroup>
            </FormControl>
          )
        }
        return (
          <FormControlLabel
            control={<Checkbox size="small" />}
            label={<Typography variant="body2">{displayLabel}{required ? ' *' : ''}</Typography>}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            label={displayLabel}
            required={required}
            disabled
            value="Unknown Field Type"
            size="small"
          />
        );
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        border: '1px dashed #bdbdbd',
        borderRadius: 2,
        bgcolor: '#fafafa',
        position: 'relative'
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          top: -10,
          left: 10,
          bgcolor: '#fafafa',
          px: 1,
          color: '#757575',
          fontWeight: 'bold'
        }}
      >
        Live Preview
      </Typography>
      {renderField()}
    </Box>
  );
};

export default BankFieldPreview;
